import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Card,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { renderMarkdown, getMarkdownStyles } from "../utils/markdownRenderer";

const API_URL = "/raporo";
const SIG_URL = "/signature";

/* ═══════════════════════════════════════════════════
   STATUT (STATUS) HELPERS
═══════════════════════════════════════════════════ */
const STATUT_CONFIG = {
  "Inyandiko itaruzura":    { color: "#6b7280", bg: "rgba(107,114,128,0.15)", label: "Inyandiko itaruzura",    icon: "bi-pencil" },
  "Itegereje Umudugudu":   { color: "#f59e0b", bg: "rgba(245,158,11,0.15)",  label: "Itegereje Umudugudu",   icon: "bi-hourglass-split" },
  "Yasubijwe":             { color: "#ef4444", bg: "rgba(239,68,68,0.15)",   label: "Yasubijwe",             icon: "bi-arrow-return-left" },
  "Itegereje Akagari":     { color: "#3b82f6", bg: "rgba(59,130,246,0.15)",  label: "Itegereje Akagari",     icon: "bi-send" },
  "Yemejwe Burundu":       { color: "#10b981", bg: "rgba(16,185,129,0.15)",  label: "Yemejwe Burundu",       icon: "bi-check-circle-fill" },
};

const StatutBadge = ({ statut }) => {
  const cfg = STATUT_CONFIG[statut] || STATUT_CONFIG["Inyandiko itaruzura"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "0.3rem",
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}40`,
      borderRadius: 20, padding: "3px 10px",
      fontSize: "0.65rem", fontWeight: 700,
    }}>
      <i className={`bi ${cfg.icon}`} style={{ fontSize: "0.6rem" }} />
      {cfg.label}
    </span>
  );
};

/* ═══════════════════════════════════════════════════
   RESPONSIVE HOOK
═══════════════════════════════════════════════════ */
function useResponsive() {
  const getInfo = () => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    return {
      w,
      h,
      isTinyPhone: w < 360,
      isSmallPhone: w >= 360 && w < 390,
      isMedPhone: w >= 390 && w < 430,
      isLargePhone: w >= 430 && w < 576,
      isMobile: w < 576,
      isSmallTablet: w >= 576 && w < 768,
      isTablet: w >= 576 && w < 992,
      isLargeTablet: w >= 768 && w < 992,
      isDesktop: w >= 992,
      isLargeDesktop: w >= 1200,
      isUltraWide: w >= 1400,
      isLandscape: w > h,
      isPortrait: h >= w,
    };
  };
  const [info, setInfo] = useState(getInfo());
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => setInfo(getInfo()), 80);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return info;
}

/* ═══════════════════════════════════════════════════
   THEME
═══════════════════════════════════════════════════ */
const theme = {
  bg: "#f4f6fb",
  cardBg: "#ffffff",
  cardBorder: "#e2e8f0",
  cardBg2: "#f8fafc",
  text: "#1e293b",
  textMuted: "#64748b",
  textDim: "#94a3b8",
  primary: "#0047AB",
  primaryDark: "#003a8c",
  primaryGlow: "rgba(0,71,171,0.20)",
  primaryLight: "rgba(0,71,171,0.08)",
  success: "#15803d",
  successGlow: "rgba(21,128,61,0.20)",
  warning: "#b45309",
  warningGlow: "rgba(180,83,9,0.20)",
  danger: "#dc2626",
  dangerGlow: "rgba(220,38,38,0.20)",
  info: "#0891b2",
  infoGlow: "rgba(8,145,178,0.20)",
  border: "#e2e8f0",
  borderLight: "#e5e9f0",
  borderHover: "#cbd5e1",
};

/* ═══════════════════════════════════════════════════
   SIGNATURE PAD COMPONENT
═══════════════════════════════════════════════════ */
const SignaturePad = ({ onSave, onClose, scr, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const currentStroke = useRef([]);
  const [activeTab, setActiveTab] = useState("draw");
  const [penColor, setPenColor] = useState("#000080");
  const [penWidth, setPenWidth] = useState(2);
  const [sigFullname, setSigFullname] = useState(
    existingSignature?.fullname || ""
  );
  const [sigTitle, setSigTitle] = useState(
    existingSignature?.title_position || ""
  );
  const [typedText, setTypedText] = useState("");
  const [selectedFont, setSelectedFont] = useState(
    "'Dancing Script', cursive"
  );
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const fs = {
    body: scr.isMobile ? "0.85rem" : "0.9rem",
    small: scr.isMobile ? "0.72rem" : "0.8rem",
    input: scr.isMobile ? "16px" : "0.9rem",
  };

  // Canvas setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    canvas.width = container.offsetWidth;
    canvas.height = 200;
  }, [activeTab]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    setIsDrawing(true);
    setHasDrawn(true);
    currentStroke.current = [{ ...pos, color: penColor, width: penWidth }];
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const doDraw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    currentStroke.current.push({
      ...pos,
      color: penColor,
      width: penWidth,
    });
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDraw = () => {
    if (isDrawing && currentStroke.current.length > 0) {
      setStrokes((prev) => [...prev, [...currentStroke.current]]);
      currentStroke.current = [];
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setStrokes([]);
    setHasDrawn(false);
  };

  const undoStroke = () => {
    if (strokes.length === 0) return;
    const newStrokes = strokes.slice(0, -1);
    setStrokes(newStrokes);
    // Redraw
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    newStrokes.forEach((stroke) => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = stroke[0].color;
      ctx.lineWidth = stroke[0].width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    });
    if (newStrokes.length === 0) setHasDrawn(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Ifoto ni nini cyane. Max 5MB.");
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveSignature = async (type) => {
    if (!sigFullname.trim()) {
      alert("Andika amazina yawe!");
      return;
    }
    setSaving(true);
    try {
      if (type === "draw") {
        if (!hasDrawn) { alert("Banza wandike umukono wawe!"); setSaving(false); return; }
        const canvas = canvasRef.current;
        const signatureImage = canvas.toDataURL("image/png");
        const res = await axiosInstance.post(`${SIG_URL}/save`, {
          signature_image: signatureImage, signature_type: "Drawn",
          fullname: sigFullname, title_position: sigTitle,
        });
        onSave(res.data);
      } else if (type === "upload") {
        if (!uploadFile) { alert("Hitamo ifoto y'umukono!"); setSaving(false); return; }
        const fd = new FormData();
        fd.append("signature_file", uploadFile);
        fd.append("fullname", sigFullname);
        fd.append("title_position", sigTitle);
        const res = await axiosInstance.post(`${SIG_URL}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onSave(res.data);
      } else if (type === "type") {
        if (!typedText.trim()) { alert("Andika amazina yawe!"); setSaving(false); return; }
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = 400; tempCanvas.height = 100;
        const tCtx = tempCanvas.getContext("2d");
        tCtx.fillStyle = "white"; tCtx.fillRect(0, 0, 400, 100);
        const fontFamily = selectedFont.replace(/'/g, "");
        tCtx.font = `bold 36px ${fontFamily}`;
        tCtx.fillStyle = "#000080"; tCtx.textAlign = "center"; tCtx.textBaseline = "middle";
        tCtx.fillText(typedText, 200, 50);
        const signatureImage = tempCanvas.toDataURL("image/png");
        const res = await axiosInstance.post(`${SIG_URL}/save`, {
          signature_image: signatureImage, signature_type: "Typed",
          fullname: sigFullname, title_position: sigTitle,
        });
        onSave(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.error || "Habaye ikosa mu kubika umukono");
    } finally {
      setSaving(false);
    }
  };

  const tabStyle = (isActive) => ({
    padding: scr.isMobile ? "8px 12px" : "10px 20px",
    border: "none",
    background: isActive ? theme.primaryLight : "transparent",
    color: isActive ? theme.primary : theme.textMuted,
    cursor: "pointer",
    fontWeight: isActive ? 700 : 500,
    fontSize: fs.small,
    borderBottom: isActive
      ? `3px solid ${theme.primary}`
      : "3px solid transparent",
    transition: "all 0.2s",
    flex: 1,
    textAlign: "center",
    borderRadius: "8px 8px 0 0",
  });

  const fonts = [
    { name: "Signature", value: "'Dancing Script', cursive" },
    { name: "Elegant", value: "'Great Vibes', cursive" },
    { name: "Handwritten", value: "'Caveat', cursive" },
  ];

  return (
    <div>
      {/* Name & Title */}
      <Row className="g-2 mb-3">
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label
              style={{
                ...labelStyle,
                fontSize: fs.small,
              }}
            >
              Amazina yose <span style={{ color: theme.danger }}>*</span>
            </Form.Label>
            <Form.Control
              value={sigFullname}
              onChange={(e) => setSigFullname(e.target.value)}
              placeholder="Amazina yawe yombi"
              style={{ ...inputStyle, fontSize: fs.input, minHeight: 44 }}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group>
            <Form.Label style={{ ...labelStyle, fontSize: fs.small }}>
              Umwanya / Inshingano
            </Form.Label>
            <Form.Control
              value={sigTitle}
              onChange={(e) => setSigTitle(e.target.value)}
              placeholder="urugero: Umuyobozi w'Umudugudu"
              style={{ ...inputStyle, fontSize: fs.input, minHeight: 44 }}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          borderBottom: `2px solid ${theme.cardBorder}`,
          marginBottom: "1rem",
        }}
      >
        <button
          style={tabStyle(activeTab === "draw")}
          onClick={() => setActiveTab("draw")}
        >
          ✏️ Kwandika
        </button>
        <button
          style={tabStyle(activeTab === "upload")}
          onClick={() => setActiveTab("upload")}
        >
          📤 Ifoto
        </button>
        <button
          style={tabStyle(activeTab === "type")}
          onClick={() => setActiveTab("type")}
        >
          ⌨️ Izina
        </button>
      </div>

      {/* Draw Tab */}
      {activeTab === "draw" && (
        <div>
          {/* Drawing tools */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              marginBottom: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
              }}
            >
              <span
                style={{
                  fontSize: fs.small,
                  color: theme.textMuted,
                  fontWeight: 600,
                }}
              >
                Ibara:
              </span>
              <input
                type="color"
                value={penColor}
                onChange={(e) => setPenColor(e.target.value)}
                style={{
                  width: 32,
                  height: 32,
                  border: `2px solid ${theme.borderLight}`,
                  borderRadius: "50%",
                  cursor: "pointer",
                  padding: 0,
                  background: "transparent",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
              }}
            >
              <span
                style={{
                  fontSize: fs.small,
                  color: theme.textMuted,
                  fontWeight: 600,
                }}
              >
                Ubunini:
              </span>
              <input
                type="range"
                min="1"
                max="6"
                value={penWidth}
                onChange={(e) => setPenWidth(parseInt(e.target.value))}
                style={{ width: 80 }}
              />
              <span
                style={{ fontSize: fs.small, color: theme.textDim }}
              >
                {penWidth}px
              </span>
            </div>
          </div>

          {/* Canvas */}
          <div
            style={{
              position: "relative",
              border: `2px dashed ${hasDrawn ? theme.primary : theme.borderLight}`,
              borderRadius: 12,
              background: "#fafafa",
              overflow: "hidden",
              marginBottom: "0.75rem",
              transition: "border-color 0.3s",
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                display: "block",
                width: "100%",
                height: 200,
                cursor: "crosshair",
                touchAction: "none",
              }}
              onMouseDown={startDraw}
              onMouseMove={doDraw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={doDraw}
              onTouchEnd={stopDraw}
            />
            {!hasDrawn && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#bbb",
                  fontSize: scr.isMobile ? "0.85rem" : "1rem",
                  pointerEvents: "none",
                  textAlign: "center",
                }}
              >
                ☝️ Andika umukono wawe hano
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            <Button
              onClick={clearCanvas}
              style={{
                background: `${theme.danger}15`,
                border: `1px solid ${theme.danger}40`,
                color: theme.danger,
                borderRadius: 12,
                fontSize: fs.small,
                padding: "8px 14px",
                fontWeight: 600,
              }}
            >
              🗑️ Siba
            </Button>
            <Button
              onClick={undoStroke}
              disabled={strokes.length === 0}
              style={{
                background: `${theme.textMuted}10`,
                border: `1px solid ${theme.borderLight}`,
                color: theme.textMuted,
                borderRadius: 12,
                fontSize: fs.small,
                padding: "8px 14px",
                fontWeight: 600,
                opacity: strokes.length === 0 ? 0.4 : 1,
              }}
            >
              ↩️ Subiza
            </Button>
            <Button
              onClick={() => saveSignature("draw")}
              disabled={saving || !hasDrawn}
              style={{
                background: `linear-gradient(135deg, ${theme.success}, #059669)`,
                border: "none",
                color: "#fff",
                borderRadius: 12,
                fontSize: fs.small,
                padding: "8px 16px",
                fontWeight: 700,
                flex: 1,
                opacity: !hasDrawn ? 0.5 : 1,
              }}
            >
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "💾 Bika Umukono"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === "upload" && (
        <div>
          <div
            onClick={() =>
              document.getElementById("sigFileInputPad").click()
            }
            style={{
              border: `2px dashed ${uploadPreview ? theme.success : theme.borderLight}`,
              borderRadius: 12,
              padding: scr.isMobile ? "1.5rem" : "2rem",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s",
              marginBottom: "0.75rem",
              background: uploadPreview ? `${theme.success}05` : "transparent",
            }}
          >
            {uploadPreview ? (
              <div>
                <img
                  src={uploadPreview}
                  alt="Preview"
                  style={{
                    maxWidth: 250,
                    maxHeight: 100,
                    marginBottom: "0.5rem",
                  }}
                />
                <p
                  style={{
                    color: theme.success,
                    fontSize: fs.small,
                    margin: 0,
                  }}
                >
                  ✅ Ifoto yashyizwemo. Kanda kugirango uhindure.
                </p>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    fontSize: "2.5rem",
                    marginBottom: "0.5rem",
                    opacity: 0.5,
                  }}
                >
                  📁
                </div>
                <p style={{ color: theme.textMuted, fontSize: fs.body }}>
                  Kanda hano cyangwa ukurure ifoto y'umukono wawe
                </p>
                <p
                  style={{
                    color: theme.textDim,
                    fontSize: fs.small,
                  }}
                >
                  PNG, JPG, SVG (Max 5MB)
                </p>
              </div>
            )}
            <input
              type="file"
              id="sigFileInputPad"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </div>

          <Button
            onClick={() => saveSignature("upload")}
            disabled={saving || !uploadFile}
            style={{
              background: `linear-gradient(135deg, ${theme.success}, #059669)`,
              border: "none",
              color: "#fff",
              borderRadius: 12,
              fontSize: fs.body,
              padding: "10px 20px",
              fontWeight: 700,
              width: "100%",
              opacity: !uploadFile ? 0.5 : 1,
            }}
          >
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "💾 Bika Umukono"
            )}
          </Button>
        </div>
      )}

      {/* Type Tab */}
      {activeTab === "type" && (
        <div>
          <Form.Control
            value={typedText}
            onChange={(e) => setTypedText(e.target.value)}
            placeholder="Andika amazina yawe..."
            style={{
              ...inputStyle,
              fontSize: "1.3rem",
              textAlign: "center",
              marginBottom: "0.75rem",
              minHeight: 50,
            }}
          />

          {/* Font options */}
          <div
            style={{
              display: "flex",
              gap: "0.4rem",
              flexWrap: "wrap",
              marginBottom: "0.75rem",
            }}
          >
            {fonts.map((f) => (
              <button
                key={f.value}
                onClick={() => setSelectedFont(f.value)}
                style={{
                  padding: "8px 14px",
                  border: `2px solid ${selectedFont === f.value ? theme.primary : theme.borderLight}`,
                  borderRadius: 10,
                  background:
                    selectedFont === f.value
                      ? theme.primaryLight
                      : "transparent",
                  color:
                    selectedFont === f.value ? theme.primary : theme.textMuted,
                  cursor: "pointer",
                  fontFamily: f.value,
                  fontSize: "1.1rem",
                  transition: "all 0.2s",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                {f.name}
              </button>
            ))}
          </div>

          {/* Preview */}
          <div
            style={{
              padding: "1.5rem",
              textAlign: "center",
              border: `1px solid ${theme.borderLight}`,
              borderRadius: 12,
              background: "#fafafa",
              marginBottom: "0.75rem",
              minHeight: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontFamily: selectedFont,
                fontSize: "2rem",
                color: "#000080",
                fontWeight: 700,
              }}
            >
              {typedText || "Preview"}
            </span>
          </div>

          <Button
            onClick={() => saveSignature("type")}
            disabled={saving || !typedText.trim()}
            style={{
              background: `linear-gradient(135deg, ${theme.success}, #059669)`,
              border: "none",
              color: "#fff",
              borderRadius: 12,
              fontSize: fs.body,
              padding: "10px 20px",
              fontWeight: 700,
              width: "100%",
              opacity: !typedText.trim() ? 0.5 : 1,
            }}
          >
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "💾 Bika Umukono"
            )}
          </Button>
        </div>
      )}

      {/* Google Font import for typed signatures */}
      <link
        href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Caveat:wght@600&display=swap"
        rel="stylesheet"
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN RAPORO COMPONENT
═══════════════════════════════════════════════════ */
const Raporo = () => {
  const scr = useResponsive();
  const navigate = useNavigate();
  const formRef = useRef(null);

  // ── Existing State ──
  const [raporoList, setRaporoList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentRaporo, setCurrentRaporo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterVillage, setFilterVillage] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCard, setExpandedCard] = useState(null);
  const [showFilters, setShowFilters] = useState(!scr.isMobile);

  // ── NEW: Signature State ──
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [userSignature, setUserSignature] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [autoSign, setAutoSign] = useState(true);
  const [signingReportId, setSigningReportId] = useState(null);
  const [showSigningModal, setShowSigningModal] = useState(false);

  const itemsPerPage = scr.isMobile ? 6 : scr.isTablet ? 8 : 10;

  const [stats, setStats] = useState({
    total: 0,
    totalResidents: 0,
    totalHouses: 0,
    totalVulnerable: 0,
  });

  // ── Detect user role from JWT for auto-suggest ──
  const userRole = (() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.role || null;
    } catch { return null; }
  })();

  // Auto-suggest recipient based on role
  const defaultRecipient = userRole === "Isibo" ? "Umudugudu"
    : userRole === "Umudugudu" ? "Akagari" : "";

  const initialForm = {
    umutwe_wa_raporo: "",
    ibisobanuro: "",
    itariki_ya_raporo: "",
    umubare_w_abaturage: "",
    umubare_w_ingo: "",
    abanyantege_nke: "",
    yakozwe_na: "",
    umudugudu: "",
    uwo_raporo_igenewe: defaultRecipient,
    uwo_raporo_igenewe_custom: "",
    attachment: null,
  };
  const [form, setForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});

  // ── Helpers ──
  const calculateStats = useCallback((data) => {
    if (!data?.length) {
      setStats({
        total: 0,
        totalResidents: 0,
        totalHouses: 0,
        totalVulnerable: 0,
      });
      return;
    }
    setStats({
      total: data.length,
      totalResidents: data.reduce(
        (s, r) => s + Number(r.umubare_w_abaturage || 0),
        0
      ),
      totalHouses: data.reduce(
        (s, r) => s + Number(r.umubare_w_ingo || 0),
        0
      ),
      totalVulnerable: data.reduce(
        (s, r) => s + Number(r.abanyantege_nke || 0),
        0
      ),
    });
  }, []);

  const showAlertMsg = useCallback((msg, variant = "success") => {
    setAlert({ show: true, message: msg, variant });
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "" }),
      4500
    );
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setFormErrors({});
    setCurrentRaporo(null);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "attachment" ? files[0] : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  // Resolve final recipient value
  const resolveRecipient = () =>
    form.uwo_raporo_igenewe === "Andika"
      ? form.uwo_raporo_igenewe_custom.trim()
      : form.uwo_raporo_igenewe;

  // Recipient → statut mapping
  const recipientToStatut = (recipient) => {
    if (recipient === "Umudugudu") return "Itegereje Umudugudu";
    if (recipient === "Akagari")   return "Itegereje Akagari";
    return "Inyandiko itaruzura";
  };

  const validateForm = () => {
    const errors = {};
    if (!form.umutwe_wa_raporo.trim())
      errors.umutwe_wa_raporo = "Umutwe wa raporo ni ngombwa";
    if (!form.ibisobanuro.trim())
      errors.ibisobanuro = "Ibisobanuro ni ngombwa";
    if (!form.itariki_ya_raporo)
      errors.itariki_ya_raporo = "Itariki ya raporo ni ngombwa";
    const recipient = resolveRecipient();
    if (!recipient)
      errors.uwo_raporo_igenewe = "Hitamo uwo raporo igenewe mbere yo gukomeza";
    // Prevent Isibo from sending directly to Akagari
    if (userRole === "Isibo" && recipient === "Akagari")
      errors.uwo_raporo_igenewe = "Isibo ntishobora kohereza raporo kuri Akagari burundu. Ohereza kuri Umudugudu";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Data Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API_URL);
      setRaporoList(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
      setFilteredData(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
      calculateStats(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch (err) {
      console.error("fetchData error:", err);
      showAlertMsg("Habaye ikosa mu kugira raporo", "danger");
    } finally {
      setLoading(false);
    }
  }, [calculateStats, showAlertMsg]);

  // ── Fetch user's signature ──
  const fetchSignature = useCallback(async () => {
    try {
      setSignatureLoading(true);
      const res = await axiosInstance.get(`${SIG_URL}/my-signature`);
      if (res.data.hasSignature) setUserSignature(res.data.signature);
      else setUserSignature(null);
    } catch (err) {
      console.error("fetchSignature error:", err);
    } finally {
      setSignatureLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchSignature();
  }, [fetchData, fetchSignature]);

  // ── Filter/Sort ──
  useEffect(() => {
    let res = [...raporoList];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      res = res.filter(
        (r) =>
          r.umutwe_wa_raporo?.toLowerCase().includes(t) ||
          r.ibisobanuro?.toLowerCase().includes(t) ||
          r.yakozwe_na?.toLowerCase().includes(t) ||
          r.umudugudu?.toLowerCase().includes(t)
      );
    }
    if (filterVillage !== "all")
      res = res.filter((r) => r.umudugudu === filterVillage);
    res.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "date":
          va = new Date(a.itariki_ya_raporo || 0);
          vb = new Date(b.itariki_ya_raporo || 0);
          break;
        case "title":
          va = a.umutwe_wa_raporo?.toLowerCase() || "";
          vb = b.umutwe_wa_raporo?.toLowerCase() || "";
          break;
        case "residents":
          va = Number(a.umubare_w_abaturage || 0);
          vb = Number(b.umubare_w_abaturage || 0);
          break;
        default:
          return 0;
      }
      return sortOrder === "asc" ? (va > vb ? 1 : -1) : va < vb ? 1 : -1;
    });
    setFilteredData(res);
    setCurrentPage(1);
  }, [searchTerm, filterVillage, sortBy, sortOrder, raporoList]);

  const uniqueVillages = [
    ...new Set(raporoList.map((r) => r.umudugudu).filter(Boolean)),
  ];
  const idxLast = currentPage * itemsPerPage;
  const idxFirst = idxLast - itemsPerPage;
  const current = filteredData.slice(idxFirst, idxLast);
  const totalPg = Math.ceil(filteredData.length / itemsPerPage);

  // ── Actions ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return showAlertMsg("Uzuza neza amakuru yose", "warning");
    setSaving(true);
    try {
      const recipient = resolveRecipient();
      const fd = new FormData();
      Object.keys(form).forEach((k) => {
        if (k === "uwo_raporo_igenewe_custom") return; // skip raw custom field
        if (form[k] !== null && form[k] !== "") fd.append(k, form[k]);
      });
      // Always send resolved recipient
      fd.set("uwo_raporo_igenewe", recipient);
      // Set statut based on recipient for new reports
      if (!currentRaporo?.raporo_id) {
        fd.append("statut", recipientToStatut(recipient));
      }
      if (currentRaporo?.raporo_id) {
        // Only allow edit if statut is not locked
        const s = currentRaporo.statut;
        if (s === "Yemejwe Burundu" || s === "Itegereje Akagari") {
          showAlertMsg("Raporo yashyizwe kure — ntishobora guhindurwa", "warning");
          setSaving(false);
          return;
        }
        await axiosInstance.put(`${API_URL}/${currentRaporo.raporo_id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showAlertMsg("✏️ Raporo yavuguruwe neza!", "success");
      } else {
        if (autoSign && userSignature) {
          fd.append("auto_sign", "true");
          await axiosInstance.post("/raporo-signed", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          showAlertMsg("✅ Raporo yanditswe kandi yashyizweho umukono!", "success");
        } else {
          await axiosInstance.post(API_URL, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          showAlertMsg("✅ Raporo yanditswe neza!", "success");
        }
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error("handleSubmit error:", err);
      showAlertMsg(err.response?.data?.error || "Habaye ikosa", "danger");
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow: change statut (requires sender signature) ──
  const changeStatut = useCallback(async (raporoId, newStatut) => {
    // Require sender signature before forwarding
    if (!userSignature) {
      showAlertMsg("⚠️ Shyiraho umukono wawe mbere yo kohereza raporo.", "warning");
      setShowSignatureModal(true);
      return;
    }
    // Auto-sign the report first if not yet signed
    const reportToSend = raporoList.find(r => r.raporo_id === raporoId);
    if (reportToSend && !reportToSend.signed_at) {
      try {
        const sigRes = await axiosInstance.post(`${SIG_URL}/sign-report/${raporoId}`);
        const sigData = sigRes.data.signature;
        const patchReport = (r) =>
          r.raporo_id === raporoId
            ? { ...r, signed_at: sigData.signed_at, signature_image_snapshot: sigData.signature_image, sig_image: sigData.signature_image, sig_fullname: sigData.fullname, sig_title: sigData.title_position }
            : r;
        setRaporoList(prev => prev.map(patchReport));
        setFilteredData(prev => prev.map(patchReport));
        if (selectedReport?.raporo_id === raporoId) setSelectedReport(prev => patchReport(prev));
      } catch (sigErr) {
        showAlertMsg(sigErr.response?.data?.error || "Habaye ikosa mu gushyira umukono", "danger");
        return;
      }
    }
    setSaving(true);
    try {
      await axiosInstance.put(`${API_URL}/${raporoId}/statut`, { statut: newStatut });
      showAlertMsg(`✅ Raporo yoherejwe: "${newStatut}"`, "success");
      fetchData();
      setShowDetails(false);
    } catch (err) {
      console.error("changeStatut error:", err);
      showAlertMsg(err.response?.data?.error || "Habaye ikosa mu kohereza raporo", "danger");
    } finally {
      setSaving(false);
    }
  }, [fetchData, showAlertMsg, userSignature, raporoList, selectedReport]);

  const handleEdit = (report) => {
    const s = report.statut;
    if (s === "Yemejwe Burundu" || s === "Itegereje Akagari") {
      showAlertMsg("Raporo yashyizwe kure — ntishobora guhindurwa", "warning");
      return;
    }
    const existingRecipient = report.uwo_raporo_igenewe || defaultRecipient;
    const isCustom = existingRecipient && !["Umudugudu", "Akagari", ""].includes(existingRecipient);
    setForm({
      umutwe_wa_raporo: report.umutwe_wa_raporo || "",
      ibisobanuro: report.ibisobanuro || "",
      itariki_ya_raporo: report.itariki_ya_raporo ? report.itariki_ya_raporo.split("T")[0] : "",
      umubare_w_abaturage: report.umubare_w_abaturage || "",
      umubare_w_ingo: report.umubare_w_ingo || "",
      abanyantege_nke: report.abanyantege_nke || "",
      yakozwe_na: report.yakozwe_na || "",
      umudugudu: report.umudugudu || "",
      uwo_raporo_igenewe: isCustom ? "Andika" : existingRecipient,
      uwo_raporo_igenewe_custom: isCustom ? existingRecipient : "",
      attachment: null,
    });
    setCurrentRaporo(report);
    setShowModal(true);
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await axiosInstance.delete(`${API_URL}/${deleteId}`);
      showAlertMsg("🗑️ Raporo yasibwe neza!", "success");
      fetchData();
      setShowDeleteModal(false);
    } catch (err) {
      showAlertMsg(err.response?.data?.error || "Habaye ikosa", "danger");
    } finally {
      setSaving(false);
    }
  };

  // ── Sign existing report ──
  const signReport = async (raporoId) => {
    if (!userSignature) {
      showAlertMsg("Nta mukono ubitswe. Banza ushyireho umukono wawe.", "warning");
      setShowSignatureModal(true);
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`${SIG_URL}/sign-report/${raporoId}`);
      showAlertMsg(res.data.message, "success");

      // Immediately update local state so UI reflects the signature without waiting for refetch
      const sigData = res.data.signature;
      const patchReport = (r) =>
        r.raporo_id === raporoId
          ? { ...r, signed_at: sigData.signed_at, signature_image_snapshot: sigData.signature_image, sig_image: sigData.signature_image, sig_fullname: sigData.fullname, sig_title: sigData.title_position }
          : r;

      setRaporoList((prev) => prev.map(patchReport));
      setFilteredData((prev) => prev.map(patchReport));
      if (selectedReport?.raporo_id === raporoId) {
        setSelectedReport((prev) => patchReport(prev));
      }

      setShowSigningModal(false);
      // Also refetch in background to sync with server
      fetchData();
    } catch (err) {
      showAlertMsg(err.response?.data?.error || "Habaye ikosa", "danger");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete signature ──
  const deleteSignature = async () => {
    if (!userSignature) return;
    if (!window.confirm("Uremeza ko ushaka gusiba umukono wawe?")) return;
    try {
      await axiosInstance.delete(`${SIG_URL}/${userSignature.signature_id}`);
      showAlertMsg("Umukono wasibiwe neza!", "success");
      setUserSignature(null);
      fetchSignature();
    } catch (err) {
      showAlertMsg("Habaye ikosa mu gusiba umukono", "danger");
    }
  };

  // ── Handle signature save callback ──
  const handleSignatureSaved = (data) => {
    showAlertMsg(data.message || "Umukono washyizweho neza!", "success");
    setShowSignatureModal(false);
    fetchSignature();
  };

  const exportToCSV = () => {
    if (!filteredData.length)
      return showAlertMsg("Nta makuru yo kohereza", "warning");
    const headers = [
      "Umutwe wa Raporo",
      "Ibisobanuro",
      "Itariki",
      "Abaturage",
      "Ingo",
      "Abanyantege nke",
      "Yakozwe na",
      "Umudugudu",
      "Umukono",
    ];
    const rows = filteredData.map((r) => [
      r.umutwe_wa_raporo || "",
      r.ibisobanuro || "",
      r.itariki_ya_raporo || "",
      r.umubare_w_abaturage || 0,
      r.umubare_w_ingo || 0,
      r.abanyantege_nke || 0,
      r.yakozwe_na || "",
      r.umudugudu || "",
      r.signed_at ? "Yashyizweho" : "Ntirahari",
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((row) => row.map((c) => `"${c}"`).join(",")),
    ].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" })
    );
    link.download = `raporo_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    showAlertMsg("Excel yakoreshejwe neza!", "success");
  };

  // ── Responsive helpers ──
  const pad = scr.isTinyPhone
    ? "0.5rem"
    : scr.isMobile
    ? "0.75rem"
    : scr.isTablet
    ? "1rem"
    : "1.5rem";

  const fs = {
    h1: scr.isTinyPhone
      ? "1.05rem"
      : scr.isMobile
      ? "1.2rem"
      : scr.isTablet
      ? "1.4rem"
      : "1.6rem",
    h5: scr.isTinyPhone
      ? "0.82rem"
      : scr.isMobile
      ? "0.9rem"
      : "1rem",
    body: scr.isTinyPhone
      ? "0.78rem"
      : scr.isMobile
      ? "0.85rem"
      : "0.9rem",
    small: scr.isTinyPhone
      ? "0.65rem"
      : scr.isMobile
      ? "0.72rem"
      : "0.8rem",
    stat: scr.isTinyPhone
      ? "1.1rem"
      : scr.isSmallPhone
      ? "1.25rem"
      : scr.isMobile
      ? "1.4rem"
      : scr.isTablet
      ? "1.8rem"
      : "2.25rem",
    input: scr.isMobile ? "16px" : "0.9rem",
  };
  const bR = scr.isTinyPhone ? 10 : scr.isMobile ? 14 : 20;
  const btnH = 46;

  const statCards = [
    {
      title: "Raporo Zose",
      val: stats.total,
      color: theme.primary,
      icon: "file-earmark-text-fill",
      glow: theme.primaryGlow,
    },
    {
      title: "Abaturage",
      val: stats.totalResidents,
      color: theme.success,
      icon: "people-fill",
      glow: theme.successGlow,
    },
    {
      title: "Ingo",
      val: stats.totalHouses,
      color: theme.info,
      icon: "houses-fill",
      glow: theme.infoGlow,
    },
    {
      title: "Urubyiruko",
      val: stats.totalVulnerable,
      color: theme.warning,
      icon: "exclamation-triangle-fill",
      glow: theme.warningGlow,
    },
  ];

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <div
      style={{
        background: theme.bg,
        color: theme.text,
        minHeight: "100vh",
        minHeight: "100dvh",
        padding: pad,
        fontFamily:
          "'Nunito Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        WebkitTextSizeAdjust: "100%",
        WebkitFontSmoothing: "antialiased",
        overflowX: "hidden",
      }}
    >
      <style>{getMarkdownStyles() + "\n    .ibikorwa-md-content .md-renderer-body { color: " + theme.textMuted + "; }\n  "}</style>
      {/* ════════════ HEADER ════════════ */}
      <header
        style={{
          display: "flex",
          flexDirection: scr.isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: scr.isMobile ? "stretch" : "center",
          marginBottom: scr.isMobile ? "1rem" : "1.5rem",
          gap: scr.isMobile ? "0.75rem" : "1rem",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            style={{
              fontWeight: 800,
              fontSize: fs.h1,
              color: theme.primary,
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              lineHeight: 1.3,
            }}
          >
            <i className="bi bi-file-earmark-text-fill" />
            <span>Raporo</span>
          </h2>
          <p
            style={{
              color: theme.textMuted,
              margin: "0.15rem 0 0",
              fontSize: fs.small,
            }}
          >
            Gucunga no gukurikirana raporo z'umudugudu
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
              border: "none",
              borderRadius: 14,
              padding: scr.isMobile
                ? "0.5rem 0.8rem"
                : "0.7rem 1.2rem",
              fontWeight: 700,
              fontSize: fs.body,
              boxShadow: `0 4px 15px ${theme.primaryGlow}`,
              flex: scr.isMobile ? 1 : "unset",
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.3rem",
              whiteSpace: "nowrap",
            }}
          >
            <i className="bi bi-plus-circle" />
            <span>{scr.isTinyPhone ? "+" : "Ongeramo"}</span>
          </Button>

          {/* NEW: Signature Button */}
          <Button
            onClick={() => setShowSignatureModal(true)}
            style={{
              background: userSignature
                ? `linear-gradient(135deg, ${theme.success}, #059669)`
                : `linear-gradient(135deg, ${theme.warning}, #d97706)`,
              border: "none",
              borderRadius: 14,
              padding: scr.isMobile
                ? "0.5rem 0.8rem"
                : "0.7rem 1.2rem",
              fontWeight: 700,
              fontSize: fs.body,
              boxShadow: `0 4px 15px ${userSignature ? theme.successGlow : theme.warningGlow}`,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.3rem",
              whiteSpace: "nowrap",
              color: "#fff",
            }}
          >
            <i className="bi bi-vector-pen" />
            <span>
              {scr.isTinyPhone
                ? "✍"
                : userSignature
                ? "Umukono"
                : "Shyiraho Umukono"}
            </span>
          </Button>

          <Button
            onClick={fetchData}
            aria-label="Refresh"
            style={{
              background: "transparent",
              border: `1px solid ${theme.borderLight}`,
              borderRadius: 14,
              color: theme.textMuted,
              minWidth: 44,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.5rem",
            }}
          >
            <i
              className="bi bi-arrow-clockwise"
              style={{ fontSize: "1.1rem" }}
            />
          </Button>

          <Button
            onClick={exportToCSV}
            aria-label="Export Excel"
            style={{
              background: "transparent",
              border: `1px solid ${theme.borderLight}`,
              borderRadius: 14,
              color: theme.success,
              minWidth: 44,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.5rem",
              gap: "0.3rem",
            }}
          >
            <i className="bi bi-download" />
            {!scr.isMobile && (
              <span style={{ fontSize: fs.small }}>Excel</span>
            )}
          </Button>
        </div>
      </header>

      {/* ════════════ ALERT ════════════ */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "" })
          }
          style={{
            borderRadius: bR,
            border: "none",
            marginBottom: scr.isMobile ? "0.75rem" : "1rem",
            fontSize: fs.body,
            padding: scr.isMobile
              ? "0.5rem 0.75rem"
              : "0.75rem 1.25rem",
            boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
          }}
        >
          {alert.message}
        </Alert>
      )}

      {/* ════════════ SIGNATURE STATUS BANNER ════════════ */}
      <div
        style={{
          background: userSignature
            ? `${theme.success}10`
            : `${theme.warning}10`,
          border: `1px solid ${userSignature ? `${theme.success}30` : `${theme.warning}30`}`,
          borderRadius: bR,
          padding: scr.isMobile ? "0.6rem 0.75rem" : "0.7rem 1rem",
          marginBottom: scr.isMobile ? "0.75rem" : "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flex: 1,
            minWidth: 0,
          }}
        >
          {signatureLoading ? (
            <Spinner
              animation="border"
              size="sm"
              style={{ color: theme.textMuted }}
            />
          ) : userSignature ? (
            <>
              <img
                src={userSignature.signature_image}
                alt="Umukono"
                style={{
                  height: scr.isMobile ? 28 : 36,
                  maxWidth: scr.isMobile ? 80 : 120,
                  objectFit: "contain",
                  borderRadius: 6,
                  border: `1px solid ${theme.success}40`,
                  background: "#fff",
                  padding: "2px 4px",
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: fs.small,
                    fontWeight: 700,
                    color: theme.success,
                    lineHeight: 1.3,
                  }}
                >
                  ✅ Umukono ubitswe
                </div>
                <div
                  style={{
                    fontSize:
                      scr.isTinyPhone ? "0.55rem" : "0.65rem",
                    color: theme.textDim,
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {userSignature.fullname}
                  {userSignature.title_position &&
                    ` · ${userSignature.title_position}`}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div
                style={{
                  fontSize: fs.small,
                  fontWeight: 700,
                  color: theme.warning,
                }}
              >
                ⚠️ Nta mukono ubitswe
              </div>
              <div
                style={{
                  fontSize:
                    scr.isTinyPhone ? "0.55rem" : "0.65rem",
                  color: theme.textDim,
                }}
              >
                Shyiraho umukono kugirango ushyirwe kuri raporo
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "0.3rem", flexShrink: 0 }}>
          <Button
            size="sm"
            onClick={() => setShowSignatureModal(true)}
            style={{
              background: userSignature
                ? `${theme.primary}15`
                : `linear-gradient(135deg, ${theme.warning}, #d97706)`,
              border: userSignature
                ? `1px solid ${theme.primary}30`
                : "none",
              color: userSignature ? theme.primary : "#fff",
              borderRadius: 10,
              fontSize: scr.isTinyPhone ? "0.6rem" : "0.7rem",
              fontWeight: 600,
              padding: "4px 10px",
              minHeight: 30,
            }}
          >
            {userSignature ? "Hindura" : "Shyiraho"}
          </Button>
          {userSignature && (
            <Button
              size="sm"
              onClick={deleteSignature}
              style={{
                background: `${theme.danger}15`,
                border: `1px solid ${theme.danger}30`,
                color: theme.danger,
                borderRadius: 10,
                fontSize: "0.7rem",
                padding: "4px 8px",
                minHeight: 30,
              }}
            >
              <i className="bi bi-trash" />
            </Button>
          )}
        </div>
      </div>

      {/* ════════════ STATS ════════════ */}
      <Row
        className="g-2 g-sm-3"
        style={{ marginBottom: scr.isMobile ? "0.75rem" : "1.25rem" }}
      >
        {statCards.map((s, i) => (
          <Col key={i} xs={6} lg={3}>
            <div
              style={{
                background: `linear-gradient(145deg, ${theme.cardBg} 0%, ${theme.cardBg2} 100%)`,
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: bR,
                padding: scr.isTinyPhone
                  ? "0.6rem 0.5rem"
                  : scr.isMobile
                  ? "0.8rem 0.7rem"
                  : scr.isTablet
                  ? "1rem"
                  : "1.3rem",
                textAlign: "center",
                transition: "all 0.3s",
                height: "100%",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-20px",
                  right: "-20px",
                  width: scr.isMobile ? 50 : 80,
                  height: scr.isMobile ? 50 : 80,
                  borderRadius: "50%",
                  background: s.color,
                  opacity: 0.06,
                  filter: "blur(20px)",
                }}
              />
              <div
                style={{
                  width: scr.isTinyPhone
                    ? 36
                    : scr.isMobile
                    ? 42
                    : scr.isTablet
                    ? 52
                    : 62,
                  height: scr.isTinyPhone
                    ? 36
                    : scr.isMobile
                    ? 42
                    : scr.isTablet
                    ? 52
                    : 62,
                  borderRadius: "50%",
                  border: `2px solid ${s.color}40`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  marginBottom: scr.isMobile ? "0.4rem" : "0.6rem",
                  background: `${s.color}10`,
                }}
              >
                <i
                  className={`bi bi-${s.icon}`}
                  style={{
                    fontSize: scr.isTinyPhone
                      ? "0.85rem"
                      : scr.isMobile
                      ? "1rem"
                      : scr.isTablet
                      ? "1.2rem"
                      : "1.4rem",
                    color: s.color,
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: fs.stat,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: 1.2,
                  marginBottom: "0.15rem",
                }}
              >
                {s.val.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: scr.isTinyPhone
                    ? "0.58rem"
                    : scr.isMobile
                    ? "0.65rem"
                    : "0.78rem",
                  fontWeight: 600,
                  color: theme.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  lineHeight: 1.3,
                }}
              >
                {s.title}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ════════════ FILTERS ════════════ */}
      <div
        style={{
          background: `linear-gradient(145deg, ${theme.cardBg} 0%, ${theme.cardBg2} 100%)`,
          border: `1px solid ${theme.cardBorder}`,
          borderRadius: bR,
          marginBottom: scr.isMobile ? "0.75rem" : "1.25rem",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: scr.isMobile ? "0.6rem" : "0.8rem 1rem",
            display: "flex",
            gap: "0.4rem",
            alignItems: "center",
          }}
        >
          <InputGroup style={{ flex: 1 }}>
            <InputGroup.Text
              style={{
                background: theme.bg,
                borderColor: theme.borderLight,
                color: theme.textMuted,
                padding: scr.isMobile
                  ? "0.35rem 0.5rem"
                  : "0.5rem 0.75rem",
              }}
            >
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Shakisha raporo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                backgroundColor: theme.cardBg,
                borderColor: theme.borderLight,
                color: theme.text,
                fontSize: fs.input,
                minHeight: 44,
                padding: scr.isMobile
                  ? "0.4rem 0.6rem"
                  : "0.5rem 0.9rem",
              }}
            />
            {searchTerm && (
              <Button
                onClick={() => setSearchTerm("")}
                style={{
                  background: theme.bg,
                  borderColor: theme.borderLight,
                  color: theme.textMuted,
                  padding: "0 0.6rem",
                }}
              >
                <i className="bi bi-x" />
              </Button>
            )}
          </InputGroup>
          {scr.isMobile && (
            <Button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                background: showFilters
                  ? theme.primaryLight
                  : "transparent",
                border: `1px solid ${showFilters ? theme.primary : theme.borderLight}`,
                borderRadius: 12,
                color: showFilters ? theme.primary : theme.textMuted,
                minWidth: 44,
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                transition: "all 0.2s",
              }}
            >
              <i className="bi bi-funnel" />
            </Button>
          )}
        </div>
        {(showFilters || !scr.isMobile) && (
          <div
            style={{
              padding: scr.isMobile
                ? "0 0.6rem 0.6rem"
                : "0 1rem 0.8rem",
              display: "flex",
              gap: "0.4rem",
              flexWrap: "wrap",
              borderTop: scr.isMobile
                ? `1px solid ${theme.cardBorder}`
                : "none",
              paddingTop: scr.isMobile ? "0.6rem" : 0,
            }}
          >
            <Form.Select
              value={filterVillage}
              onChange={(e) => setFilterVillage(e.target.value)}
              style={{
                backgroundColor: theme.cardBg,
                borderColor: theme.borderLight,
                color: theme.text,
                borderRadius: 12,
                fontSize: fs.input,
                minHeight: 44,
                flex: 1,
                minWidth: scr.isMobile ? "100%" : 140,
                padding: "0.5rem 0.75rem",
              }}
            >
              <option value="all">Umudugudu wose</option>
              {uniqueVillages.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                backgroundColor: theme.cardBg,
                borderColor: theme.borderLight,
                color: theme.text,
                borderRadius: 12,
                fontSize: fs.input,
                minHeight: 44,
                flex: 1,
                minWidth: scr.isMobile ? "45%" : 120,
                padding: "0.5rem 0.75rem",
              }}
            >
              <option value="date">Itariki</option>
              <option value="title">Umutwe</option>
              <option value="residents">Abaturage</option>
            </Form.Select>
            <Button
              onClick={() =>
                setSortOrder(sortOrder === "asc" ? "desc" : "asc")
              }
              style={{
                background: "transparent",
                border: `1px solid ${theme.borderLight}`,
                borderRadius: 12,
                color: theme.textMuted,
                minWidth: 44,
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              <i
                className={`bi bi-sort-${sortOrder === "asc" ? "up" : "down"}`}
              />
            </Button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
                padding: "0 0.5rem",
                fontSize: fs.small,
                color: theme.textMuted,
                whiteSpace: "nowrap",
              }}
            >
              <Badge
                bg={null}
                style={{
                  background: theme.primaryLight,
                  color: theme.primary,
                  fontWeight: 700,
                  fontSize: fs.small,
                }}
              >
                {filteredData.length}
              </Badge>
              <span>raporo</span>
            </div>
          </div>
        )}
      </div>

      {/* ════════════ CONTENT ════════════ */}
      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: scr.isMobile ? "2.5rem 1rem" : "4rem",
          }}
        >
          <Spinner
            animation="border"
            style={{
              color: theme.primary,
              width: 50,
              height: 50,
              borderWidth: 3,
            }}
          />
          <p
            style={{
              color: theme.textMuted,
              marginTop: "1rem",
              fontSize: fs.body,
            }}
          >
            Gutegura amakuru...
          </p>
        </div>
      ) : filteredData.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: scr.isMobile ? "2rem" : "3rem",
            background: `linear-gradient(145deg, ${theme.cardBg}, ${theme.cardBg2})`,
            border: `1px dashed ${theme.borderLight}`,
            borderRadius: bR,
          }}
        >
          <i
            className="bi bi-file-earmark-x"
            style={{
              fontSize: scr.isMobile ? "2.5rem" : "3.5rem",
              color: theme.borderLight,
            }}
          />
          <h5
            style={{
              color: theme.textMuted,
              marginTop: "0.75rem",
              fontSize: fs.h5,
            }}
          >
            {searchTerm
              ? `"${searchTerm}" ntibyo byabonetse`
              : "Nta raporo zihari"}
          </h5>
          <p style={{ color: theme.textDim, fontSize: fs.small }}>
            Tangira wandike raporo nshya
          </p>
        </div>
      ) : scr.isMobile ? (
        /* ════════════ MOBILE CARD VIEW ════════════ */
        <div
          style={{
            maxHeight: scr.isLandscape ? "55vh" : "68vh",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "0.5rem",
          }}
        >
          {current.map((r, idx) => (
            <div
              key={r.raporo_id}
              style={{
                background: `linear-gradient(145deg, ${theme.cardBg} 0%, ${theme.cardBg2} 100%)`,
                border: `1px solid ${expandedCard === r.raporo_id ? theme.primary : theme.cardBorder}`,
                borderRadius: bR,
                marginBottom: scr.isTinyPhone ? "0.3rem" : "0.5rem",
                overflow: "hidden",
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxShadow:
                  expandedCard === r.raporo_id
                    ? `0 0 20px ${theme.primaryGlow}`
                    : "none",
              }}
            >
              {/* Card Header */}
              <div
                onClick={() =>
                  setExpandedCard(
                    expandedCard === r.raporo_id ? null : r.raporo_id
                  )
                }
                style={{
                  padding: scr.isTinyPhone ? "0.6rem" : "0.8rem",
                  display: "flex",
                  alignItems: "center",
                  gap: scr.isTinyPhone ? "0.4rem" : "0.6rem",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <div
                  style={{
                    width: scr.isTinyPhone ? 28 : 34,
                    height: scr.isTinyPhone ? 28 : 34,
                    borderRadius: 8,
                    background: theme.primaryLight,
                    color: theme.primary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: scr.isTinyPhone ? "0.7rem" : "0.8rem",
                    flexShrink: 0,
                  }}
                >
                  {idxFirst + idx + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: scr.isTinyPhone
                        ? "0.8rem"
                        : "0.88rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.3,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                    }}
                  >
                    {r.umutwe_wa_raporo}
                    {r.signed_at && (
                      <span title="Yashyizweho umukono" style={{ fontSize: "0.6rem", color: theme.success }}>✍️</span>
                    )}
                  </div>
                  <div
                    style={{
                      color: theme.textDim,
                      fontSize: scr.isTinyPhone
                        ? "0.6rem"
                        : "0.68rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      lineHeight: 1.3,
                    }}
                  >
                    <i
                      className="bi bi-calendar3"
                      style={{ fontSize: "0.55rem" }}
                    />
                    {r.itariki_ya_raporo
                      ? new Date(
                          r.itariki_ya_raporo
                        ).toLocaleDateString("rw-RW")
                      : "—"}
                    {r.umudugudu && (
                      <>
                        <span>•</span>
                        <span>{r.umudugudu}</span>
                      </>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "0.25rem",
                    flexShrink: 0,
                  }}
                >
                  <Badge
                    bg={null}
                    style={{
                      background: `${theme.info}20`,
                      color: theme.info,
                      fontSize: scr.isTinyPhone
                        ? "0.55rem"
                        : "0.62rem",
                      padding: "3px 6px",
                      fontWeight: 600,
                    }}
                  >
                    {r.umubare_w_abaturage || 0}
                  </Badge>
                  <Badge
                    bg={null}
                    style={{
                      background: `${theme.warning}20`,
                      color: theme.warning,
                      fontSize: scr.isTinyPhone
                        ? "0.55rem"
                        : "0.62rem",
                      padding: "3px 6px",
                      fontWeight: 600,
                    }}
                  >
                    {r.abanyantege_nke || 0}
                  </Badge>
                </div>
                <i
                  className={`bi bi-chevron-${expandedCard === r.raporo_id ? "up" : "down"}`}
                  style={{
                    color: theme.textDim,
                    fontSize: "0.65rem",
                    flexShrink: 0,
                  }}
                />
              </div>

              {/* Expanded Content */}
              {expandedCard === r.raporo_id && (
                <div
                  style={{
                    padding: scr.isTinyPhone
                      ? "0 0.6rem 0.6rem"
                      : "0 0.8rem 0.8rem",
                    animation: "fadeSlide 0.25s ease",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "0.35rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {[
                      {
                        label: "Abaturage",
                        val: r.umubare_w_abaturage || 0,
                        color: theme.info,
                      },
                      {
                        label: "Ingo",
                        val: r.umubare_w_ingo || 0,
                        color: theme.textMuted,
                      },
                      {
                        label: "Urubyiruko",
                        val: r.abanyantege_nke || 0,
                        color: theme.warning,
                      },
                    ].map((item, j) => (
                      <div
                        key={j}
                        style={{
                          background: "rgba(0,0,0,0.3)",
                          borderRadius: 10,
                          padding: "0.4rem",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: scr.isTinyPhone
                              ? "0.55rem"
                              : "0.6rem",
                            color: theme.textDim,
                            textTransform: "uppercase",
                            fontWeight: 600,
                            letterSpacing: "0.03em",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.label}
                        </div>
                        <div
                          style={{
                            fontSize: scr.isTinyPhone
                              ? "0.9rem"
                              : "1rem",
                            fontWeight: 800,
                            color: item.color,
                            lineHeight: 1.3,
                          }}
                        >
                          {item.val}
                        </div>
                      </div>
                    ))}
                  </div>

                  {r.ibisobanuro && (
                    <div
                      style={{
                        background: "rgba(0,0,0,0.3)",
                        borderRadius: 10,
                        padding: "0.5rem",
                        marginBottom: "0.5rem",
                        fontSize: scr.isTinyPhone
                          ? "0.7rem"
                          : "0.75rem",
                        color: theme.textMuted,
                        lineHeight: 1.5,
                        maxHeight: "4rem",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {r.ibisobanuro.substring(0, 120)}
                      {r.ibisobanuro.length > 120 && "..."}
                    </div>
                  )}

                  {r.yakozwe_na && (
                    <div style={{ fontSize: scr.isTinyPhone ? "0.62rem" : "0.68rem", color: theme.textDim, marginBottom: "0.4rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                      <i className="bi bi-person" /><span>{r.yakozwe_na}</span>
                    </div>
                  )}

                  {/* Uwo Raporo Igenewe */}
                  {r.uwo_raporo_igenewe && (
                    <div style={{ marginBottom: "0.4rem", display: "flex", alignItems: "center", gap: "0.3rem", fontSize: scr.isTinyPhone ? "0.62rem" : "0.68rem" }}>
                      <i className="bi bi-person-check-fill" style={{ color: theme.primary, fontSize: "0.6rem" }} />
                      <span style={{ color: theme.textDim, fontWeight: 600 }}>Uwo bireba:</span>
                      <span style={{ color: theme.primary, fontWeight: 700 }}>{r.uwo_raporo_igenewe}</span>
                    </div>
                  )}

                  {/* Statut badge */}
                  <div style={{ marginBottom: "0.4rem" }}>
                    <StatutBadge statut={r.statut || "Inyandiko itaruzura"} />
                  </div>

                  {/* Signature status on card */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      marginBottom: "0.5rem",
                      fontSize: scr.isTinyPhone
                        ? "0.6rem"
                        : "0.68rem",
                    }}
                  >
                    {r.signed_at ? (
                      <Badge
                        bg={null}
                        style={{
                          background: `${theme.success}20`,
                          color: theme.success,
                          fontSize: "0.6rem",
                          padding: "3px 8px",
                          fontWeight: 600,
                        }}
                      >
                        ✍️ Yashyizweho umukono ·{" "}
                        {new Date(r.signed_at).toLocaleDateString(
                          "rw-RW"
                        )}
                      </Badge>
                    ) : (
                      <Badge
                        bg={null}
                        style={{
                          background: `${theme.warning}15`,
                          color: theme.warning,
                          fontSize: "0.6rem",
                          padding: "3px 8px",
                          fontWeight: 600,
                        }}
                      >
                        Ntirahari umukono
                      </Badge>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: "flex", gap: "0.35rem" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/view-report/${r.raporo_id}`); }}
                      style={{ flex: 1, height: 38, borderRadius: 10, background: `${theme.info}15`, border: `1px solid ${theme.info}30`, color: theme.info, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem", cursor: "pointer", fontWeight: 600, fontSize: scr.isTinyPhone ? "0.68rem" : "0.75rem", WebkitTapHighlightColor: "transparent" }}
                    >
                      <i className="bi bi-eye" />Reba
                    </button>

                    {/* Ohereza button for Isibo */}
                    {(r.statut === "Inyandiko itaruzura" || r.statut === "Yasubijwe" || !r.statut) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); changeStatut(r.raporo_id, "Itegereje Umudugudu"); }}
                        style={{ flex: 1, height: 38, borderRadius: 10, background: `${theme.warning}15`, border: `1px solid ${theme.warning}30`, color: theme.warning, display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem", cursor: "pointer", fontWeight: 600, fontSize: scr.isTinyPhone ? "0.68rem" : "0.75rem", WebkitTapHighlightColor: "transparent" }}
                      >
                        <i className="bi bi-send" />Ohereza
                      </button>
                    )}

                    {/* Sign button for unsigned reports */}
                    {!r.signed_at && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          signReport(r.raporo_id);
                        }}
                        style={{
                          flex: 1,
                          height: 38,
                          borderRadius: 10,
                          background: `${theme.success}15`,
                          border: `1px solid ${theme.success}30`,
                          color: theme.success,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "0.3rem",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: scr.isTinyPhone
                            ? "0.68rem"
                            : "0.75rem",
                          WebkitTapHighlightColor: "transparent",
                        }}
                      >
                        <i className="bi bi-vector-pen" />
                        Sinya
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(r);
                      }}
                      style={{
                        flex: 1,
                        height: 38,
                        borderRadius: 10,
                        background: `${theme.primary}15`,
                        border: `1px solid ${theme.primary}30`,
                        color: theme.primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.3rem",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: scr.isTinyPhone
                          ? "0.68rem"
                          : "0.75rem",
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <i className="bi bi-pencil" />
                      Hindura
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(r.raporo_id);
                      }}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: `${theme.danger}15`,
                        border: `1px solid ${theme.danger}30`,
                        color: theme.danger,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* ════════════ TABLET & DESKTOP TABLE ════════════ */
        <div
          style={{
            background: theme.bg,
            border: `1px solid ${theme.cardBorder}`,
            borderRadius: bR,
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
          }}
        >
          <div
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              maxHeight: scr.isLandscape ? "55vh" : "65vh",
              overflowY: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: scr.isSmallTablet
                  ? 700
                  : scr.isLargeTablet
                  ? 800
                  : 900,
              }}
            >
              <thead>
                <tr>
                  {[
                    "#",
                    "Umutwe wa Raporo",
                    "Uwo Igenewe",
                    "Itariki",
                    "Abaturage",
                    "Ingo",
                    "Urubyiruko",
                    "Umudugudu",
                    "Statut",
                    "Umukono",
                    "Ibikorwa",
                  ].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        padding: scr.isSmallTablet
                          ? "0.7rem 0.6rem"
                          : "0.9rem 0.75rem",
                        color: theme.textMuted,
                        textTransform: "uppercase",
                        fontSize: scr.isSmallTablet
                          ? "0.6rem"
                          : "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        borderBottom: `2px solid ${theme.cardBorder}`,
                        textAlign: [0, 2, 3, 4, 5, 6, 8, 9, 10].includes(i)
                          ? "center"
                          : "left",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                        background: theme.cardBg,
                        zIndex: 2,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {current.map((r, idx) => (
                  <tr
                    key={r.raporo_id}
                    style={{ transition: "background 0.15s" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = `${theme.primary}08`)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "transparent")
                    }
                  >
                    <td
                      style={{
                        padding: scr.isSmallTablet
                          ? "0.7rem 0.6rem"
                          : "0.9rem 0.75rem",
                        borderBottom: `1px solid ${theme.cardBorder}`,
                        color: theme.textDim,
                        textAlign: "center",
                        fontSize: scr.isSmallTablet
                          ? "0.75rem"
                          : "0.8rem",
                      }}
                    >
                      {idxFirst + idx + 1}
                    </td>
                    <td
                      style={{
                        padding: scr.isSmallTablet
                          ? "0.7rem 0.6rem"
                          : "0.9rem 0.75rem",
                        borderBottom: `1px solid ${theme.cardBorder}`,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: scr.isSmallTablet
                          ? "0.8rem"
                          : "0.85rem",
                        maxWidth: 220,
                      }}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.umutwe_wa_raporo}
                      </div>
                      {r.yakozwe_na && (
                        <div
                          style={{
                            fontSize: "0.68rem",
                            color: theme.textDim,
                            marginTop: "0.15rem",
                          }}
                        >
                          <i
                            className="bi bi-person me-1"
                            style={{ fontSize: "0.6rem" }}
                          />
                          {r.yakozwe_na}
                        </div>
                      )}
                    </td>
                      {/* Uwo Igenewe column */}
                      <td style={{ padding: scr.isSmallTablet ? "0.7rem 0.6rem" : "0.9rem 0.75rem", borderBottom: `1px solid ${theme.cardBorder}`, textAlign: "center" }}>
                        {r.uwo_raporo_igenewe ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", background: `${theme.primary}15`, color: theme.primary, border: `1px solid ${theme.primary}30`, borderRadius: 20, padding: "2px 8px", fontSize: "0.65rem", fontWeight: 700 }}>
                            <i className="bi bi-person-check" style={{ fontSize: "0.6rem" }} />
                            {r.uwo_raporo_igenewe}
                          </span>
                        ) : <span style={{ color: theme.textDim, fontSize: "0.7rem" }}>—</span>}
                      </td>

                      <td style={{ padding: scr.isSmallTablet ? "0.7rem 0.6rem" : "0.9rem 0.75rem", borderBottom: `1px solid ${theme.cardBorder}`, color: theme.textMuted, textAlign: "center", fontSize: scr.isSmallTablet ? "0.75rem" : "0.8rem", whiteSpace: "nowrap" }}>
                        {r.itariki_ya_raporo ? new Date(r.itariki_ya_raporo).toLocaleDateString("rw-RW") : "—"}
                      </td>
                    {[
                      {
                        val: r.umubare_w_abaturage,
                        bg: `${theme.info}20`,
                        color: theme.info,
                      },
                      {
                        val: r.umubare_w_ingo,
                        bg: `${theme.textMuted}15`,
                        color: theme.textMuted,
                      },
                      {
                        val: r.abanyantege_nke,
                        bg: `${theme.warning}20`,
                        color: theme.warning,
                      },
                    ].map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: scr.isSmallTablet
                            ? "0.7rem 0.6rem"
                            : "0.9rem 0.75rem",
                          borderBottom: `1px solid ${theme.cardBorder}`,
                          textAlign: "center",
                        }}
                      >
                        <Badge
                          pill
                          bg={null}
                          style={{
                            background: cell.bg,
                            color: cell.color,
                            fontWeight: 700,
                            fontSize: scr.isSmallTablet
                              ? "0.68rem"
                              : "0.72rem",
                            padding: "4px 10px",
                          }}
                        >
                          {cell.val || 0}
                        </Badge>
                      </td>
                    ))}
                    <td
                      style={{
                        padding: scr.isSmallTablet
                          ? "0.7rem 0.6rem"
                          : "0.9rem 0.75rem",
                        borderBottom: `1px solid ${theme.cardBorder}`,
                        color: theme.textMuted,
                        fontSize: scr.isSmallTablet
                          ? "0.75rem"
                          : "0.8rem",
                      }}
                    >
                      {r.umudugudu || "—"}
                    </td>

                    {/* Statut column */}
                    <td style={{ padding: scr.isSmallTablet ? "0.7rem 0.6rem" : "0.9rem 0.75rem", borderBottom: `1px solid ${theme.cardBorder}`, textAlign: "center" }}>
                      <StatutBadge statut={r.statut || "Inyandiko itaruzura"} />
                    </td>

                    {/* Signature column */}
                    <td
                      style={{
                        padding: scr.isSmallTablet
                          ? "0.7rem 0.6rem"
                          : "0.9rem 0.75rem",
                        borderBottom: `1px solid ${theme.cardBorder}`,
                        textAlign: "center",
                      }}
                    >
                      {r.signed_at ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "0.15rem",
                          }}
                        >
                          {r.signature_image_snapshot && (
                            <img
                              src={r.signature_image_snapshot}
                              alt="Sig"
                              style={{
                                height: 24,
                                maxWidth: 70,
                                objectFit: "contain",
                                borderRadius: 4,
                                background: "#fff",
                                padding: "1px 3px",
                              }}
                            />
                          )}
                          <Badge
                            bg={null}
                            style={{
                              background: `${theme.success}20`,
                              color: theme.success,
                              fontSize: "0.58rem",
                              padding: "2px 6px",
                              fontWeight: 600,
                            }}
                          >
                            ✅{" "}
                            {new Date(
                              r.signed_at
                            ).toLocaleDateString("rw-RW")}
                          </Badge>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => signReport(r.raporo_id)}
                          style={{
                            background: `${theme.warning}15`,
                            border: `1px solid ${theme.warning}30`,
                            color: theme.warning,
                            borderRadius: 8,
                            fontSize: "0.65rem",
                            padding: "3px 8px",
                            fontWeight: 600,
                          }}
                        >
                          ✍ Sinya
                        </Button>
                      )}
                    </td>

                    {/* Actions column */}
                    <td
                      style={{
                        padding: scr.isSmallTablet
                          ? "0.7rem 0.6rem"
                          : "0.9rem 0.75rem",
                        borderBottom: `1px solid ${theme.cardBorder}`,
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "0.3rem",
                          justifyContent: "center",
                        }}
                      >
                        {[
                          {
                            icon: "eye",
                            color: theme.info,
                            onClick: () => navigate(`/view-report/${r.raporo_id}`),
                          },
                          {
                            icon: "pencil",
                            color: theme.primary,
                            onClick: () => handleEdit(r),
                          },
                          {
                            icon: "trash",
                            color: theme.danger,
                            onClick: () =>
                              openDeleteModal(r.raporo_id),
                          },
                        ].map((btn, bi) => (
                          <Button
                            key={bi}
                            size="sm"
                            onClick={btn.onClick}
                            style={{
                              width: scr.isSmallTablet ? 30 : 34,
                              height: scr.isSmallTablet ? 30 : 34,
                              padding: 0,
                              borderRadius: 8,
                              background: `${btn.color}12`,
                              border: `1px solid ${btn.color}30`,
                              color: btn.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s",
                            }}
                          >
                            <i
                              className={`bi bi-${btn.icon}`}
                              style={{
                                fontSize: scr.isSmallTablet
                                  ? "0.65rem"
                                  : "0.75rem",
                              }}
                            />
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ PAGINATION ════════════ */}
      {totalPg > 1 && (
        <div
          style={{
            display: "flex",
            flexDirection: scr.isTinyPhone ? "column" : "row",
            justifyContent: "space-between",
            alignItems: scr.isTinyPhone ? "stretch" : "center",
            marginTop: scr.isMobile ? "0.75rem" : "1.25rem",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              color: theme.textMuted,
              fontSize: fs.small,
              textAlign: scr.isTinyPhone ? "center" : "left",
            }}
          >
            {idxFirst + 1}–{Math.min(idxLast, filteredData.length)}{" "}
            kuri {filteredData.length}
          </span>
          <div
            style={{
              display: "flex",
              gap: "0.2rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              style={{
                ...paginationBtnStyle(scr),
                opacity: currentPage === 1 ? 0.4 : 1,
              }}
            >
              <i className="bi bi-chevron-double-left" />
            </button>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              style={{
                ...paginationBtnStyle(scr),
                opacity: currentPage === 1 ? 0.4 : 1,
              }}
            >
              <i className="bi bi-chevron-left" />
            </button>
            {getPaginationRange(
              currentPage,
              totalPg,
              scr.isMobile ? 3 : 5
            ).map((p, i) =>
              p === "..." ? (
                <span
                  key={`dots-${i}`}
                  style={{
                    ...paginationBtnStyle(scr),
                    background: "transparent",
                    border: "none",
                    color: theme.textDim,
                    cursor: "default",
                  }}
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  style={{
                    ...paginationBtnStyle(scr),
                    background:
                      p === currentPage
                        ? `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`
                        : theme.cardBg,
                    color:
                      p === currentPage ? "#fff" : theme.textMuted,
                    borderColor:
                      p === currentPage
                        ? theme.primary
                        : theme.borderLight,
                    fontWeight: p === currentPage ? 700 : 500,
                  }}
                >
                  {p}
                </button>
              )
            )}
            <button
              disabled={currentPage === totalPg}
              onClick={() => setCurrentPage(currentPage + 1)}
              style={{
                ...paginationBtnStyle(scr),
                opacity: currentPage === totalPg ? 0.4 : 1,
              }}
            >
              <i className="bi bi-chevron-right" />
            </button>
            <button
              disabled={currentPage === totalPg}
              onClick={() => setCurrentPage(totalPg)}
              style={{
                ...paginationBtnStyle(scr),
                opacity: currentPage === totalPg ? 0.4 : 1,
              }}
            >
              <i className="bi bi-chevron-double-right" />
            </button>
          </div>
        </div>
      )}

      {/* ════════════ DELETE MODAL ════════════ */}
      <Modal
        show={showDeleteModal}
        onHide={() => !saving && setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <div
          style={{
            background: theme.cardBg,
            borderRadius: scr.isMobile ? 16 : 24,
            border: `1px solid ${theme.cardBorder}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: scr.isMobile ? "1.5rem 1rem" : "2rem",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: scr.isMobile ? 56 : 72,
                height: scr.isMobile ? 56 : 72,
                borderRadius: "50%",
                background: `${theme.danger}15`,
                border: `2px solid ${theme.danger}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem",
              }}
            >
              <i
                className="bi bi-exclamation-triangle-fill"
                style={{
                  fontSize: scr.isMobile ? "1.5rem" : "2rem",
                  color: theme.danger,
                }}
              />
            </div>
            <h4
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: scr.isMobile ? "1.1rem" : "1.25rem",
                marginBottom: "0.5rem",
              }}
            >
              Emeza Gusiba
            </h4>
            <p
              style={{
                color: theme.textMuted,
                fontSize: fs.body,
                lineHeight: 1.6,
                margin: "0 0 1.2rem",
              }}
            >
              Urashaka koko gusiba iyi raporo?
              <br />
              <strong style={{ color: theme.danger }}>
                Iki gikorwa ntikigarurwa.
              </strong>
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                flexDirection: scr.isMobile
                  ? "column-reverse"
                  : "row",
                justifyContent: "center",
              }}
            >
              <Button
                disabled={saving}
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: "transparent",
                  border: `1px solid ${theme.borderLight}`,
                  color: theme.text,
                  borderRadius: 14,
                  padding: "0.6rem 1.2rem",
                  fontWeight: 600,
                  fontSize: fs.body,
                  minHeight: btnH,
                  width: scr.isMobile ? "100%" : "auto",
                }}
              >
                Bireke
              </Button>
              <Button
                disabled={saving}
                onClick={confirmDelete}
                style={{
                  background: `linear-gradient(135deg, ${theme.danger}, #dc2626)`,
                  border: "none",
                  borderRadius: 14,
                  padding: "0.6rem 1.2rem",
                  fontWeight: 700,
                  fontSize: fs.body,
                  boxShadow: `0 4px 15px ${theme.dangerGlow}`,
                  minHeight: btnH,
                  width: scr.isMobile ? "100%" : "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.35rem",
                }}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" />{" "}
                    Tegereza...
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash-fill" /> Emeza, Siba
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* ════════════ ADD/EDIT MODAL (with auto-sign toggle) ════════════ */}
      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        size="lg"
        backdrop="static"
        centered
        fullscreen={scr.isMobile ? true : undefined}
      >
        <div
          style={{
            background: theme.cardBg,
            borderRadius: scr.isMobile ? 0 : 24,
            border: scr.isMobile
              ? "none"
              : `1px solid ${theme.cardBorder}`,
            overflow: "hidden",
            height: scr.isMobile ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Modal Header */}
          <div
            style={{
              background: currentRaporo?.raporo_id
                ? `linear-gradient(135deg, #f59e0b, #d97706)`
                : `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
              padding: scr.isMobile
                ? "0.8rem 1rem"
                : "1.2rem 1.5rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <h5
              style={{
                margin: 0,
                color: "#fff",
                fontWeight: 700,
                fontSize: fs.h5,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
            >
              <i
                className={`bi bi-${
                  currentRaporo?.raporo_id ? "pencil" : "plus-circle"
                }-fill`}
              />
              {currentRaporo?.raporo_id
                ? "Hindura Raporo"
                : "Ongeramo Raporo"}
            </h5>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Modal Body */}
          <div
            style={{
              padding: scr.isMobile ? "1rem" : "1.5rem",
              overflowY: "auto",
              flex: 1,
              background: theme.bg,
            }}
          >
            <Form onSubmit={handleSubmit} ref={formRef}>
              <Row className="g-2 g-md-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Umutwe wa Raporo{" "}
                      <span style={{ color: theme.danger }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="umutwe_wa_raporo"
                      value={form.umutwe_wa_raporo}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.umutwe_wa_raporo}
                      placeholder="Umutwe wa raporo"
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                        minHeight: 44,
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.umutwe_wa_raporo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Ibisobanuro{" "}
                      <span style={{ color: theme.danger }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={scr.isMobile ? 3 : 4}
                      name="ibisobanuro"
                      value={form.ibisobanuro}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.ibisobanuro}
                      placeholder="Ibisobanuro by'inyongera..."
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.ibisobanuro}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Itariki{" "}
                      <span style={{ color: theme.danger }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="itariki_ya_raporo"
                      value={form.itariki_ya_raporo}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.itariki_ya_raporo}
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                        minHeight: 44,
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.itariki_ya_raporo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Umudugudu
                    </Form.Label>
                    <Form.Control
                      name="umudugudu"
                      value={form.umudugudu}
                      onChange={handleInputChange}
                      placeholder="Umudugudu"
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                        minHeight: 44,
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={4}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Abaturage
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="umubare_w_abaturage"
                      value={form.umubare_w_abaturage}
                      onChange={handleInputChange}
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                        minHeight: 44,
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={4}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Ingo
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="umubare_w_ingo"
                      value={form.umubare_w_ingo}
                      onChange={handleInputChange}
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                        minHeight: 44,
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={4}>
                  <Form.Group>
                    <Form.Label
                      style={{ ...labelStyle, fontSize: fs.small }}
                    >
                      Urubyiruko
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="abanyantege_nke"
                      value={form.abanyantege_nke}
                      onChange={handleInputChange}
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      style={{
                        ...inputStyle,
                        fontSize: fs.input,
                        minHeight: 44,
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label style={{ ...labelStyle, fontSize: fs.small }}>
                      Yakozwe na
                    </Form.Label>
                    <Form.Control
                      name="yakozwe_na"
                      value={form.yakozwe_na}
                      onChange={handleInputChange}
                      placeholder="Izina ry'umwanditsi"
                      style={{ ...inputStyle, fontSize: fs.input, minHeight: 44 }}
                    />
                  </Form.Group>
                </Col>

                {/* ══ UWO RAPORO IGENEWE ══ */}
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label style={{ ...labelStyle, fontSize: fs.small }}>
                      Uwo Raporo Igenewe{" "}
                      <span style={{ color: theme.danger }}>*</span>
                    </Form.Label>
                    <Form.Select
                      name="uwo_raporo_igenewe"
                      value={form.uwo_raporo_igenewe}
                      onChange={handleInputChange}
                      isInvalid={!!formErrors.uwo_raporo_igenewe}
                      style={{ ...inputStyle, fontSize: fs.input, minHeight: 44, cursor: "pointer" }}
                    >
                      <option value="">Hitamo uwo igenewe...</option>
                      <option value="Umudugudu">Umudugudu</option>
                      {userRole !== "Isibo" && (
                        <option value="Akagari">Akagari</option>
                      )}
                      <option value="Andika">Andika...</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {formErrors.uwo_raporo_igenewe}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Custom recipient input */}
                {form.uwo_raporo_igenewe === "Andika" && (
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label style={{ ...labelStyle, fontSize: fs.small }}>
                        Shyiramo izina ry'uwo igenewe
                      </Form.Label>
                      <Form.Control
                        name="uwo_raporo_igenewe_custom"
                        value={form.uwo_raporo_igenewe_custom}
                        onChange={handleInputChange}
                        placeholder="Andika izina ry'uwo raporo igenewe..."
                        style={{ ...inputStyle, fontSize: fs.input, minHeight: 44 }}
                      />
                    </Form.Group>
                  </Col>
                )}

                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label style={{ ...labelStyle, fontSize: fs.small }}>
                      Attachment
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="attachment"
                      onChange={handleInputChange}
                      style={{ ...inputStyle, fontSize: fs.input, minHeight: 44 }}
                    />
                  </Form.Group>
                </Col>

                {/* NEW: Auto-sign toggle (only for new reports) */}
                {!currentRaporo?.raporo_id && (
                  <Col xs={12}>
                    <div
                      style={{
                        background: userSignature
                          ? `${theme.success}08`
                          : `${theme.warning}08`,
                        border: `1px solid ${userSignature ? `${theme.success}25` : `${theme.warning}25`}`,
                        borderRadius: 14,
                        padding: scr.isMobile
                          ? "0.75rem"
                          : "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {userSignature ? (
                          <>
                            <img
                              src={userSignature.signature_image}
                              alt="Umukono"
                              style={{
                                height: 32,
                                maxWidth: 90,
                                objectFit: "contain",
                                borderRadius: 6,
                                border: `1px solid ${theme.success}40`,
                                background: "#fff",
                                padding: "2px 4px",
                              }}
                            />
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: fs.small,
                                  fontWeight: 700,
                                  color: theme.success,
                                  lineHeight: 1.3,
                                }}
                              >
                                ✍️ Shyiraho umukono mu
                                buryo bwikora
                              </div>
                              <div
                                style={{
                                  fontSize: "0.65rem",
                                  color: theme.textDim,
                                  lineHeight: 1.3,
                                }}
                              >
                                {userSignature.fullname}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div>
                            <div
                              style={{
                                fontSize: fs.small,
                                fontWeight: 700,
                                color: theme.warning,
                              }}
                            >
                              ⚠️ Nta mukono ubitswe
                            </div>
                            <div
                              style={{
                                fontSize: "0.65rem",
                                color: theme.textDim,
                              }}
                            >
                              <span
                                style={{
                                  color: theme.primary,
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={() => {
                                  setShowModal(false);
                                  setShowSignatureModal(true);
                                }}
                              >
                                Shyiraho umukono
                              </span>{" "}
                              mbere yo kubika raporo
                            </div>
                          </div>
                        )}
                      </div>

                      {userSignature && (
                        <Form.Check
                          type="switch"
                          id="auto-sign-switch"
                          checked={autoSign}
                          onChange={(e) =>
                            setAutoSign(e.target.checked)
                          }
                          label=""
                          style={{
                            transform: "scale(1.3)",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </Form>
          </div>

          {/* Modal Footer */}
          <div
            style={{
              padding: scr.isMobile
                ? "0.75rem 1rem"
                : "1rem 1.5rem",
              borderTop: `1px solid ${theme.cardBorder}`,
              display: "flex",
              gap: "0.5rem",
              flexDirection: scr.isMobile
                ? "column-reverse"
                : "row",
              justifyContent: "flex-end",
              background: theme.cardBg,
              flexShrink: 0,
            }}
          >
            <Button
              onClick={() => setShowModal(false)}
              disabled={saving}
              style={{
                background: "transparent",
                border: `1px solid ${theme.borderLight}`,
                color: theme.text,
                borderRadius: 14,
                padding: "0.6rem 1.2rem",
                fontWeight: 600,
                fontSize: fs.body,
                minHeight: btnH,
                width: scr.isMobile ? "100%" : "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.3rem",
              }}
            >
              <i className="bi bi-x-lg" /> Hagarika
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
                border: "none",
                borderRadius: 14,
                padding: "0.6rem 1.5rem",
                fontWeight: 700,
                fontSize: fs.body,
                boxShadow: `0 4px 15px ${theme.primaryGlow}`,
                minHeight: btnH,
                width: scr.isMobile ? "100%" : "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.35rem",
              }}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" />{" "}
                  Tegereza...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg" />
                  {currentRaporo?.raporo_id
                    ? "Vugurura"
                    : autoSign && userSignature
                    ? "Bika & Sinya"
                    : "Bika"}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ════════════ DETAILS MODAL (with signature display) ════════════ */}
      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="lg"
        centered
        fullscreen={scr.isMobile ? true : undefined}
      >
        <div
          style={{
            background: theme.cardBg,
            borderRadius: scr.isMobile ? 0 : 24,
            border: scr.isMobile
              ? "none"
              : `1px solid ${theme.cardBorder}`,
            overflow: "hidden",
            height: scr.isMobile ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
              padding: scr.isMobile
                ? "0.8rem 1rem"
                : "1.2rem 1.5rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <h5
              style={{
                margin: 0,
                color: "#fff",
                fontWeight: 700,
                fontSize: fs.h5,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
            >
              <i className="bi bi-file-earmark-text-fill" />
              Amakuru ya Raporo
            </h5>
            <button
              onClick={() => setShowDetails(false)}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          <div
            style={{
              padding: scr.isMobile ? "1rem" : "1.5rem",
              overflowY: "auto",
              flex: 1,
              background: theme.bg,
            }}
          >
            {selectedReport && (
              <>
                <h5
                  style={{
                    color: theme.primary,
                    fontWeight: 700,
                    fontSize: scr.isMobile ? "1rem" : "1.15rem",
                    marginBottom: "0.75rem",
                    lineHeight: 1.4,
                  }}
                >
                  {selectedReport.umutwe_wa_raporo}
                </h5>
                <div
                  style={{
                    height: 1,
                    background: theme.cardBorder,
                    marginBottom: "1rem",
                  }}
                />

                {/* Meta grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: scr.isMobile
                      ? "1fr 1fr"
                      : "1fr 1fr 1fr 1fr",
                    gap: scr.isMobile ? "0.5rem" : "0.75rem",
                    marginBottom: "1rem",
                  }}
                >
                  {[
                    {
                      label: "Itariki",
                      val: selectedReport.itariki_ya_raporo
                        ? new Date(selectedReport.itariki_ya_raporo).toLocaleDateString("rw-RW")
                        : "—",
                      icon: "calendar3",
                    },
                    {
                      label: "Umudugudu",
                      val: selectedReport.umudugudu || "—",
                      icon: "geo-alt",
                    },
                    {
                      label: "Yakozwe na",
                      val: selectedReport.yakozwe_na || "—",
                      icon: "person",
                    },
                    {
                      label: "Abaturage",
                      val: selectedReport.umubare_w_abaturage || 0,
                      icon: "people",
                      color: theme.info,
                    },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: theme.cardBg2,
                        borderRadius: 12,
                        padding: scr.isMobile
                          ? "0.6rem"
                          : "0.75rem",
                        border: `1px solid ${theme.cardBorder}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: scr.isMobile
                            ? "0.62rem"
                            : "0.7rem",
                          color: theme.textDim,
                          textTransform: "uppercase",
                          fontWeight: 600,
                          letterSpacing: "0.03em",
                          marginBottom: "0.2rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                      >
                        <i
                          className={`bi bi-${item.icon}`}
                          style={{ fontSize: "0.6rem" }}
                        />
                        {item.label}
                      </div>
                      <div
                        style={{
                          fontSize: scr.isMobile
                            ? "0.85rem"
                            : "0.95rem",
                          fontWeight: 600,
                          color: item.color || "#fff",
                        }}
                      >
                        {item.val}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Uwo Raporo Igenewe banner */}
                {selectedReport.uwo_raporo_igenewe && (
                  <div style={{
                    background: `${theme.primary}12`,
                    border: `1px solid ${theme.primary}30`,
                    borderRadius: 12,
                    padding: "0.6rem 1rem",
                    marginBottom: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: fs.body,
                  }}>
                    <i className="bi bi-person-check-fill" style={{ color: theme.primary }} />
                    <span style={{ color: theme.textMuted, fontWeight: 600 }}>Uwo bireba:</span>
                    <span style={{ color: "#fff", fontWeight: 700 }}>{selectedReport.uwo_raporo_igenewe}</span>
                  </div>
                )}

                {/* Stats badges */}
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    marginBottom: "1rem",
                  }}
                >
                  {[
                    {
                      label: "Ingo",
                      val: selectedReport.umubare_w_ingo || 0,
                      color: theme.textMuted,
                    },
                    {
                      label: "Urubyiruko",
                      val: selectedReport.abanyantege_nke || 0,
                      color: theme.warning,
                    },
                  ].map((b, idx) => (
                    <Badge
                      key={idx}
                      bg={null}
                      style={{
                        background: `${b.color}15`,
                        color: b.color,
                        fontSize: scr.isMobile
                          ? "0.72rem"
                          : "0.8rem",
                        padding: "6px 12px",
                        fontWeight: 600,
                        borderRadius: 10,
                      }}
                    >
                      {b.label}: {b.val}
                    </Badge>
                  ))}
                </div>

                {/* Description */}
                <div
                  style={{
                    fontSize: scr.isMobile ? "0.62rem" : "0.7rem",
                    color: theme.textDim,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    marginBottom: "0.3rem",
                  }}
                >
                  Ibisobanuro
                </div>
                <div
                  className="ibikorwa-md-content"
                  style={{
                    background: theme.cardBg2,
                    border: `1px solid ${theme.cardBorder}`,
                    borderRadius: 14,
                    padding: scr.isMobile ? "0.75rem" : "1rem",
                    fontSize: fs.body,
                    color: theme.textMuted,
                    lineHeight: 1.7,
                    maxHeight: scr.isMobile ? "30vh" : "40vh",
                    overflowY: "auto",
                    marginBottom: "1rem",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(selectedReport.ibisobanuro || ""),
                  }}
                />

                {/* Statut + Workflow Section */}
                <div style={{ background: theme.cardBg2, border: `1px solid ${theme.cardBorder}`, borderRadius: 14, padding: scr.isMobile ? "0.75rem" : "1rem", marginBottom: "0.75rem" }}>
                  <div style={{ fontSize: "0.7rem", color: theme.textDim, textTransform: "uppercase", fontWeight: 600, marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    <i className="bi bi-arrow-right-circle" /> Imiterere ya Raporo
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <StatutBadge statut={selectedReport.statut || "Inyandiko itaruzura"} />
                    {/* Ohereza: Isibo → Umudugudu */}
                    {(selectedReport.statut === "Inyandiko itaruzura" || selectedReport.statut === "Yasubijwe" || !selectedReport.statut) && (
                      <Button size="sm" disabled={saving} onClick={() => changeStatut(selectedReport.raporo_id, "Itegereje Umudugudu")}
                        style={{ background: `linear-gradient(135deg, ${theme.warning}, #d97706)`, border: "none", color: "#fff", borderRadius: 10, fontSize: fs.small, fontWeight: 700, padding: "5px 12px", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        {saving ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-send" /> Ohereza ku Mudugudu</>}
                      </Button>
                    )}
                    {/* Emeza / Subiza: Umudugudu */}
                    {selectedReport.statut === "Itegereje Umudugudu" && (
                      <>
                        <Button size="sm" disabled={saving} onClick={() => changeStatut(selectedReport.raporo_id, "Itegereje Akagari")}
                          style={{ background: `linear-gradient(135deg, ${theme.success}, #059669)`, border: "none", color: "#fff", borderRadius: 10, fontSize: fs.small, fontWeight: 700, padding: "5px 12px", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                          {saving ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-check-circle" /> Emeza → Akagari</>}
                        </Button>
                        <Button size="sm" disabled={saving} onClick={() => changeStatut(selectedReport.raporo_id, "Yasubijwe")}
                          style={{ background: `${theme.danger}15`, border: `1px solid ${theme.danger}30`, color: theme.danger, borderRadius: 10, fontSize: fs.small, fontWeight: 700, padding: "5px 12px", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                          {saving ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-arrow-return-left" /> Subiza Isibo</>}
                        </Button>
                      </>
                    )}
                    {/* Emeza Burundu: Akagari */}
                    {selectedReport.statut === "Itegereje Akagari" && (
                      <Button size="sm" disabled={saving} onClick={() => changeStatut(selectedReport.raporo_id, "Yemejwe Burundu")}
                        style={{ background: `linear-gradient(135deg, ${theme.success}, #059669)`, border: "none", color: "#fff", borderRadius: 10, fontSize: fs.small, fontWeight: 700, padding: "5px 12px", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        {saving ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-check-circle-fill" /> Yemeza Burundu</>}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Signature Section in Details */}
                <div
                  style={{
                    background: theme.cardBg2,
                    border: `1px solid ${selectedReport.signed_at ? `${theme.success}30` : `${theme.warning}30`}`,
                    borderRadius: 14,
                    padding: scr.isMobile ? "0.75rem" : "1rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: scr.isMobile
                        ? "0.62rem"
                        : "0.7rem",
                      color: theme.textDim,
                      textTransform: "uppercase",
                      fontWeight: 600,
                      marginBottom: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.3rem",
                    }}
                  >
                    <i className="bi bi-vector-pen" />
                    Umukono w'Umuyobozi
                  </div>

                  {selectedReport.signed_at ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: scr.isMobile
                          ? "column"
                          : "row",
                        alignItems: scr.isMobile
                          ? "flex-start"
                          : "center",
                        gap: "0.75rem",
                      }}
                    >
                      {selectedReport.signature_image_snapshot && (
                        <div
                          style={{
                            background: "#fff",
                            borderRadius: 10,
                            padding: "8px 12px",
                            border: `1px solid ${theme.success}30`,
                          }}
                        >
                          <img
                            src={
                              selectedReport.signature_image_snapshot
                            }
                            alt="Umukono"
                            style={{
                              height: scr.isMobile ? 40 : 50,
                              maxWidth: scr.isMobile ? 160 : 200,
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <div
                          style={{
                            fontSize: fs.body,
                            fontWeight: 700,
                            color: theme.success,
                            marginBottom: "0.15rem",
                          }}
                        >
                          ✅ Yashyizweho umukono
                        </div>
                        <div
                          style={{
                            fontSize: fs.small,
                            color: theme.textMuted,
                          }}
                        >
                          Itariki:{" "}
                          {new Date(
                            selectedReport.signed_at
                          ).toLocaleDateString("rw-RW")}{" "}
                          {new Date(
                            selectedReport.signed_at
                          ).toLocaleTimeString("rw-RW", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontSize: fs.body,
                          color: theme.warning,
                          fontWeight: 600,
                        }}
                      >
                        ⚠️ Iyi raporo ntirashyirwaho umukono
                      </div>
                      <Button
                        size="sm"
                        onClick={() =>
                          signReport(selectedReport.raporo_id)
                        }
                        disabled={saving}
                        style={{
                          background: `linear-gradient(135deg, ${theme.success}, #059669)`,
                          border: "none",
                          color: "#fff",
                          borderRadius: 10,
                          fontSize: fs.small,
                          fontWeight: 700,
                          padding: "6px 14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                      >
                        {saving ? (
                          <Spinner
                            animation="border"
                            size="sm"
                          />
                        ) : (
                          <>
                            <i className="bi bi-vector-pen" />
                            Shyiraho Umukono
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div
            style={{
              padding: scr.isMobile
                ? "0.75rem 1rem"
                : "1rem 1.5rem",
              borderTop: `1px solid ${theme.cardBorder}`,
              display: "flex",
              justifyContent: "flex-end",
              background: theme.cardBg,
              flexShrink: 0,
            }}
          >
            <Button
              onClick={() => setShowDetails(false)}
              style={{
                background: "transparent",
                border: `1px solid ${theme.borderLight}`,
                color: theme.text,
                borderRadius: 14,
                padding: "0.6rem 1.2rem",
                fontWeight: 600,
                fontSize: fs.body,
                minHeight: btnH,
                width: scr.isMobile ? "100%" : "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.3rem",
              }}
            >
              <i className="bi bi-x-lg" /> Funga
            </Button>
          </div>
        </div>
      </Modal>

      {/* ════════════ SIGNATURE SETUP MODAL ════════════ */}
      <Modal
        show={showSignatureModal}
        onHide={() => setShowSignatureModal(false)}
        size="lg"
        centered
        fullscreen={scr.isMobile ? true : undefined}
      >
        <div
          style={{
            background: theme.cardBg,
            borderRadius: scr.isMobile ? 0 : 24,
            border: scr.isMobile
              ? "none"
              : `1px solid ${theme.cardBorder}`,
            overflow: "hidden",
            height: scr.isMobile ? "100%" : "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.success}, #059669)`,
              padding: scr.isMobile
                ? "0.8rem 1rem"
                : "1.2rem 1.5rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <h5
              style={{
                margin: 0,
                color: "#fff",
                fontWeight: 700,
                fontSize: fs.h5,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
            >
              <i className="bi bi-vector-pen" />
              {userSignature
                ? "Hindura Umukono"
                : "Shyiraho Umukono"}
            </h5>
            <button
              onClick={() => setShowSignatureModal(false)}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              padding: scr.isMobile ? "1rem" : "1.5rem",
              overflowY: "auto",
              flex: 1,
              background: theme.bg,
            }}
          >
            {/* Current signature display */}
            {userSignature && (
              <div
                style={{
                  background: `${theme.success}08`,
                  border: `1px solid ${theme.success}25`,
                  borderRadius: 14,
                  padding: scr.isMobile ? "0.75rem" : "1rem",
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    padding: "6px 10px",
                    border: `1px solid ${theme.success}30`,
                  }}
                >
                  <img
                    src={userSignature.signature_image}
                    alt="Umukono"
                    style={{
                      height: 40,
                      maxWidth: 150,
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      color: theme.success,
                      fontSize: fs.body,
                    }}
                  >
                    ✅ Umukono uriho
                  </div>
                  <div
                    style={{
                      fontSize: fs.small,
                      color: theme.textMuted,
                    }}
                  >
                    {userSignature.fullname}
                    {userSignature.title_position &&
                      ` · ${userSignature.title_position}`}
                  </div>
                  <div
                    style={{
                      fontSize: "0.6rem",
                      color: theme.textDim,
                    }}
                  >
                    {userSignature.signature_type} ·{" "}
                    {new Date(
                      userSignature.created_at
                    ).toLocaleDateString("rw-RW")}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={deleteSignature}
                  style={{
                    background: `${theme.danger}15`,
                    border: `1px solid ${theme.danger}30`,
                    color: theme.danger,
                    borderRadius: 10,
                    fontSize: fs.small,
                    padding: "4px 10px",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  <i className="bi bi-trash me-1" />
                  Siba
                </Button>
              </div>
            )}

            {/* Signature Pad */}
            <SignaturePad
              onSave={handleSignatureSaved}
              onClose={() => setShowSignatureModal(false)}
              scr={scr}
              existingSignature={userSignature}
            />
          </div>
        </div>
      </Modal>

      {/* ════════════ GLOBAL STYLES ════════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        html { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; scroll-behavior: smooth; }
        body { overflow-x: hidden;  -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        .form-control:focus, .form-select:focus { border-color: ${theme.primary} !important; box-shadow: 0 0 0 3px ${theme.primaryGlow} !important; }
        .form-control::placeholder { color: ${theme.textDim} !important; opacity: 0.7; }
        .modal-content { background: transparent !important; border: none !important; box-shadow: none !important; }
        .modal-backdrop.show { opacity: 0.8 !important; }
        input[type="date"], input[type="number"], input[type="text"], textarea { -webkit-appearance: none; -moz-appearance: none; appearance: none; }
        input[type="date"]::-webkit-date-and-time-value { text-align: left; }
        input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${theme.bg}; }
        ::-webkit-scrollbar-thumb { background: ${theme.borderLight}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${theme.borderHover}; }
        .alert .btn-close { filter: invert(1) grayscale(100%) brightness(200%); }
        ::selection { background: ${theme.primary}; color: white; }
        @keyframes fadeSlide { from { opacity: 0; max-height: 0; transform: translateY(-5px); } to { opacity: 1; max-height: 500px; transform: translateY(0); } }
        @supports (padding: env(safe-area-inset-bottom)) { body { padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right); padding-bottom: env(safe-area-inset-bottom); } }
        @media (hover: none) and (pointer: coarse) { button:active, .btn:active { transform: scale(0.97) !important; transition: transform 0.1s !important; } .form-control, .form-select { min-height: 44px; font-size: 16px !important; } button, .btn { min-height: 44px; } }
        @media (hover: hover) and (pointer: fine) { button:hover, .btn:hover { opacity: 0.92; } }
        @media screen and (max-width: 359px) { .modal-dialog { margin: 0 !important; } }
        @media screen and (min-width: 360px) and (max-width: 389px) { .modal-dialog { margin: 0.3rem !important; } }
        @media screen and (min-width: 390px) and (max-width: 429px) { .modal-dialog { margin: 0.4rem auto !important; } }
        @media screen and (min-width: 430px) and (max-width: 575px) { .modal-dialog { margin: 0.5rem auto !important; } }
        @media screen and (min-width: 576px) and (max-width: 767px) { .modal-dialog { max-width: 90% !important; margin: 1rem auto !important; } }
        @media screen and (min-width: 768px) and (max-width: 991px) { .modal-dialog { max-width: 600px !important; margin: 1.5rem auto !important; } }
        @media screen and (min-width: 992px) { .modal-dialog { margin: 2rem auto !important; } }
        @media screen and (orientation: landscape) and (max-height: 500px) { .modal-dialog { margin: 0.2rem auto !important; } }
        .modal-fullscreen .modal-content { border-radius: 0 !important; }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
        *:focus-visible { outline: 2px solid ${theme.primary} !important; outline-offset: 2px !important; }
        @media print { body { background: white !important; color: black !important; } button, .btn, .modal { display: none !important; } }
        .form-select { background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%2364748b' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e") !important; background-repeat: no-repeat !important; background-position: right 0.75rem center !important; background-size: 16px 12px !important; }
        .form-check-input:checked { background-color: ${theme.success} !important; border-color: ${theme.success} !important; }
      `}</style>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   HELPER: Pagination Button Style
═══════════════════════════════════════════════════ */
function paginationBtnStyle(scr) {
  return {
    width: scr.isTinyPhone ? 32 : scr.isMobile ? 36 : 40,
    height: scr.isTinyPhone ? 32 : scr.isMobile ? 36 : 40,
    borderRadius: 10,
    background: "#ffffff",
    border: `1px solid #e2e8f0`,
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: scr.isMobile ? "0.75rem" : "0.85rem",
    fontWeight: 500,
    transition: "all 0.2s",
    WebkitTapHighlightColor: "transparent",
  };
}

/* ═══════════════════════════════════════════════════
   HELPER: Pagination Range
═══════════════════════════════════════════════════ */
function getPaginationRange(current, total, maxVisible = 5) {
  if (total <= maxVisible)
    return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, start + maxVisible - 1);
  if (end - start < maxVisible - 1)
    start = Math.max(1, end - maxVisible + 1);
  const range = [];
  if (start > 1) {
    range.push(1);
    if (start > 2) range.push("...");
  }
  for (let i = start; i <= end; i++) range.push(i);
  if (end < total) {
    if (end < total - 1) range.push("...");
    range.push(total);
  }
  return range;
}

/* ═══════════════════════════════════════════════════
   SHARED STYLES
═══════════════════════════════════════════════════ */
const inputStyle = {
  backgroundColor: "#ffffff",
  borderColor: "#cbd5e1",
  color: "#1e293b",
  borderRadius: 12,
  padding: "0.65rem 0.9rem",
};

const labelStyle = {
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  marginBottom: "0.3rem",
  letterSpacing: "0.03em",
};

export default Raporo;